import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Table, useLoader } from 'thermia-react-container';
import useApiClient from '../../api/api-client';
import { Device } from '../../api/api-types';
import ErrorComponent from '../../components/error-component';
import CreateKeyComponent from './create-key-component';
import DeviceUnsupportedComponent from './device-unsupported-component';
import DeviceVerificationComponent from './device-verification-component';

const AddKeyPage: FC = () => {
  const { Loader } = useLoader();
  const { getDevices } = useApiClient();
  const { t } = useTranslation('portal');

  const [initStatus, setInitStatus] = useState<'initializing' | 'error' | 'initialized'>('initializing');
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [keyCreated, setKeyCreated] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    const initialize = async () => {
      try {
        setInitStatus('initializing');
        const devices = await getDevices();
        setDevices(devices);
        setInitStatus('initialized');
      } catch {
        setInitStatus('error');
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCount]);

  useEffect(() => {
    // When the devices are updated, we need to update the selected device with the new data to reflect changes such as verified access
    if (selectedDevice) {
      const updated = devices.find((d) => d.installationId === selectedDevice.installationId);
      if (updated) {
        setSelectedDevice(updated);
      }
    }
  }, [devices, selectedDevice]);

  const columns: Column<Device>[] = useMemo(
    () => [
      {
        accessor: 'installationName',
        Header: t('portal.keys.add-key-page.table.installationName')
      },
      {
        accessor: 'model',
        Header: t('portal.keys.add-key-page.table.model')
      },
      {
        accessor: 'serialNumber',
        Header: t('portal.keys.add-key-page.table.serialnumber')
      },
      {
        accessor: 'deviceSupported',
        Header: t('portal.keys.add-key-page.table.deviceSupported')
      },
      {
        accessor: 'verifiedAccess',
        Header: t('portal.keys.add-key-page.table.verifiedAccess'),
        Cell: ({ value }) => (value ? 'true' : 'false')
      }
    ],
    [t]
  );

  const tableData = useCallback(
    async (): Promise<{ data: Device[]; totalCount: number }> => ({
      data: devices,
      totalCount: devices.length
    }),
    [devices]
  );

  const onDeviceClick = (device: Device) => {
    setSelectedDevice(device);
  };

  if (initStatus === 'initializing') return <Loader />;
  if (initStatus === 'error') return <ErrorComponent text={t('portal.keys.add-key-page.error.init')} />;
  if (devices.length === 0) return <>{t('portal.keys.add-key-page.noDevices')}</>;

  return (
    <>
      <h3>{t('portal.keys.add-key-page.title')}</h3>
      {!keyCreated && (
        <Box sx={{ marginBottom: 10 }}>
          <Table columns={columns as Column<any>[]} fetchData={tableData} onClick={onDeviceClick} />
        </Box>
      )}
      {selectedDevice && (
        <>
          <h4>
            {selectedDevice.installationName} / {selectedDevice.serialNumber}
          </h4>
          {selectedDevice.deviceSupported !== 'Supported' && <DeviceUnsupportedComponent device={selectedDevice} />}
          {selectedDevice.deviceSupported === 'Supported' && !selectedDevice.verifiedAccess && (
            <DeviceVerificationComponent
              key={selectedDevice.installationId}
              device={selectedDevice}
              onVerified={() => setRefreshCount((count) => count + 1)}
            />
          )}
          {selectedDevice.deviceSupported === 'Supported' && selectedDevice.verifiedAccess && (
            <CreateKeyComponent key={selectedDevice.installationId} device={selectedDevice} onKeyCreated={() => setKeyCreated(true)} />
          )}
        </>
      )}
    </>
  );
};

export default AddKeyPage;
